import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { availableLanguages } from '../../utils/translations';
import notificationService from '../../services/NotificationService';
import '../../styles/Onboarding.css';

// Theme-Kacheln fürs Onboarding – Labels/Emojis gespiegelt aus ThemeModal.js.
// Bitte bei Änderungen an ThemeModal.js hier mitziehen.
const THEMES = [
  {
    code: 'blue-galaxy',
    emoji: '🌌',
    name: 'Blue Galaxy',
    sub: 'Helles, blaues UI',
    swatch: 'linear-gradient(135deg, #050814 0%, #07112a 55%, #7FB6FF 100%)',
  },
  {
    code: 'goodvibes',
    emoji: '😊',
    name: 'GoodVibes',
    sub: 'Warme, freundliche Farben',
    swatch: 'linear-gradient(135deg, #FFF6DC 0%, #FFC84C 45%, #FC8797 100%)',
  },
  {
    code: 'power',
    emoji: '⚡',
    name: 'Power',
    sub: 'Kräftige Kontraste',
    swatch: 'linear-gradient(135deg, #05100a 0%, #0b1a12 55%, #48E877 100%)',
  },
  {
    code: 'purple-bubbles',
    emoji: '🫧',
    name: 'PurpleBubbles',
    sub: 'Lebendige Lila-Töne',
    swatch: 'linear-gradient(135deg, #1d0e33 0%, #2a1545 55%, #A78BFA 100%)',
  },
];

const TOTAL_STEPS = 6;

// Inline-Icons (keine Emojis, siehe Design-Vorgabe)
const IconChevronLeft = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
);
const IconCheck = ({ size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
);
const IconCloud = ({ size = 36 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 18h10a4 4 0 0 0 .5-7.97A5.5 5.5 0 0 0 7.1 9.02 4 4 0 0 0 7 18z"></path>
    <polyline points="9.5 13.5 11.3 15.3 15 11.6"></polyline>
  </svg>
);
const IconBell = ({ size = 36 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 8a6 6 0 0 1 12 0c0 4 1.5 5.5 2 6H4c.5-.5 2-2 2-6z"></path>
    <path d="M9.5 18a2.5 2.5 0 0 0 5 0"></path>
  </svg>
);
const IconGlobe = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="8"></circle>
    <path d="M4 12h16M12 4c2.2 2.2 3.3 5 3.3 8s-1.1 5.8-3.3 8c-2.2-2.2-3.3-5-3.3-8S9.8 6.2 12 4z"></path>
  </svg>
);

/**
 * Einmaliger Onboarding-Wizard, direkt nach der Registrierung gezeigt.
 * Steuerung "wird gezeigt / ist erledigt" läuft über das serverseitige
 * `onboardingCompleted`-Feld am User (siehe AuthContext.js/App.js);
 * `onFinish` (regulärer Abschluss oder "Überspringen") meldet den
 * Abschluss über AuthContext.completeOnboarding() an den Server zurück.
 */
const OnboardingFlow = ({ onFinish }) => {
  const { language, switchLanguage } = useLanguage();
  const { theme, setTheme } = useTheme();

  const [step, setStep] = useState(1);
  const [autoBackup, setAutoBackup] = useState(() => {
    try {
      return localStorage.getItem('autoBackup') !== 'false';
    } catch {
      return true;
    }
  });
  const [push, setPush] = useState(() => {
    try {
      return localStorage.getItem('pushNotificationsEnabled') !== 'false';
    } catch {
      return true;
    }
  });

  const goNext = () => {
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
    } else {
      finish();
    }
  };
  const goBack = () => setStep((s) => Math.max(1, s - 1));
  const skip = () => finish();

  const finish = () => {
    try {
      localStorage.setItem('autoBackup', autoBackup ? 'true' : 'false');
      localStorage.setItem('pushNotificationsEnabled', push ? 'true' : 'false');
    } catch {
      // localStorage nicht verfügbar (z. B. privater Modus) - Einstellungen
      // gelten dann nur für diese Sitzung
    }

    // Best effort: Browser-Berechtigung anfragen, nie den Abschluss blockieren
    if (push) {
      try {
        notificationService.requestPermission();
      } catch {
        // Berechtigung nicht verfügbar/abgelehnt - ignorieren
      }
    }

    onFinish();
  };

  const showBack = step > 1;
  const showSkip = step < TOTAL_STEPS;

  let buttonLabel = 'Weiter';
  if (step === 1) buttonLabel = "Los geht's";
  if (step === TOTAL_STEPS) buttonLabel = "Verstanden, los geht's!";

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-blob onboarding-blob-1"></div>
      <div className="onboarding-blob onboarding-blob-2"></div>

      <div className="onboarding-topbar">
        <div className="onboarding-topbar-left">
          {showBack && (
            <button className="onboarding-icon-btn" onClick={goBack} aria-label="Zurück">
              <IconChevronLeft />
            </button>
          )}
        </div>
        <div className="onboarding-topbar-right">
          {showSkip && (
            <button className="onboarding-skip-link" onClick={skip}>Überspringen</button>
          )}
        </div>
      </div>

      <div className="onboarding-dots">
        {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((n) => (
          <span
            key={n}
            className={`onboarding-dot ${n === step ? 'active' : n < step ? 'done' : ''}`}
          ></span>
        ))}
      </div>

      <div className="onboarding-content">
        {step === 1 && (
          <div className="onboarding-step">
            <img src="/images/LogoNew.png" className="onboarding-logo" alt="MyBoki Logo" />
            <h1 className="onboarding-title">Willkommen bei MyBoki.Me!</h1>
            <p className="onboarding-subtitle">Dein Assistent für Lesezeichen, Rezepte &amp; Einkaufslisten. In wenigen Schritten richten wir alles nach deinem Geschmack ein.</p>
          </div>
        )}

        {step === 2 && (
          <div className="onboarding-step onboarding-step-top">
            <h1 className="onboarding-title">Welche Sprache möchtest du nutzen?</h1>
            <p className="onboarding-subtitle">Du kannst das jederzeit in den Einstellungen ändern.</p>
            <div className="onboarding-lang-list">
              {availableLanguages.map((l) => {
                const selected = language === l.code;
                return (
                  <div
                    key={l.code}
                    className={`onboarding-lang-tile ${selected ? 'selected' : ''}`}
                    onClick={() => switchLanguage(l.code)}
                  >
                    <span className="onboarding-lang-flag">{l.flag}</span>
                    <span className="onboarding-lang-name">{l.name}</span>
                    {selected && <span className="onboarding-check"><IconCheck /></span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="onboarding-step onboarding-step-top">
            <h1 className="onboarding-title">Welches Design gefällt dir?</h1>
            <p className="onboarding-subtitle">Du kannst das Theme jederzeit unter Einstellungen → Themes wechseln.</p>
            <div className="onboarding-theme-grid">
              {THEMES.map((th) => {
                const selected = theme === th.code;
                return (
                  <div
                    key={th.code}
                    className={`onboarding-theme-tile ${selected ? 'selected' : ''}`}
                    onClick={() => setTheme(th.code)}
                  >
                    <div className="onboarding-theme-swatch" style={{ background: th.swatch }}></div>
                    <div className="onboarding-theme-name-row">
                      <span>{th.emoji}</span>
                      <span className="onboarding-theme-name">{th.name}</span>
                    </div>
                    <div className="onboarding-theme-sub">{th.sub}</div>
                    {selected && <span className="onboarding-theme-check"><IconCheck /></span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="onboarding-step">
            <div className="onboarding-icon-badge"><IconCloud /></div>
            <h1 className="onboarding-title">Automatische Datensicherung</h1>
            <p className="onboarding-subtitle">Deine Lesezeichen, Rezepte und Einkaufslisten werden regelmäßig automatisch gesichert – so geht nichts verloren.</p>
            <div className="onboarding-toggle-row">
              <div>
                <div className="onboarding-toggle-title">Automatische Backups</div>
                <div className="onboarding-toggle-status">{autoBackup ? 'Aktiviert' : 'Deaktiviert'}</div>
              </div>
              <div
                className={`onboarding-toggle-pill ${autoBackup ? 'on' : ''}`}
                onClick={() => setAutoBackup((v) => !v)}
              >
                <div className="onboarding-toggle-knob"></div>
              </div>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="onboarding-step">
            <div className="onboarding-icon-badge"><IconBell /></div>
            <h1 className="onboarding-title">Benachrichtigungen aktivieren?</h1>
            <p className="onboarding-subtitle">Erhalte Erinnerungen zu Einkaufslisten, Rezept-Ideen und fälligen Lesezeichen – genau dann, wenn du sie brauchst.</p>
            <div className="onboarding-toggle-row">
              <div>
                <div className="onboarding-toggle-title">Push-Benachrichtigungen</div>
                <div className="onboarding-toggle-status">{push ? 'Aktiviert' : 'Deaktiviert'}</div>
              </div>
              <div
                className={`onboarding-toggle-pill ${push ? 'on' : ''}`}
                onClick={() => setPush((v) => !v)}
              >
                <div className="onboarding-toggle-knob"></div>
              </div>
            </div>
          </div>
        )}

        {step === 6 && (
          <div className="onboarding-step onboarding-step-top">
            <h1 className="onboarding-title">Fast geschafft!</h1>
            <p className="onboarding-subtitle" style={{ marginBottom: 18 }}>So findest du alle Einstellungen später wieder.</p>

            <div className="onboarding-mini-header">
              <div className="onboarding-mini-header-left">
                <img src="/images/LogoNew.png" className="onboarding-mini-logo" alt="Logo" />
                <span className="onboarding-mini-title">MyBoki.Me</span>
              </div>
              <div className="onboarding-mini-hamburger-wrap">
                <div className="onboarding-mini-hamburger"><span></span><span></span><span></span></div>
                <div className="onboarding-pulse-ring"></div>
              </div>
            </div>

            <div className="onboarding-tour-connector"></div>

            <div className="onboarding-callout-card">
              <p className="onboarding-callout-text">
                Hier findest du jederzeit alle Einstellungen wieder: <strong>Menü → Einstellungen → Allgemeine Einstellungen</strong>
              </p>
              <div className="onboarding-callout-icons-row">
                <div className="onboarding-callout-icon-item"><IconGlobe />Sprache</div>
                <div className="onboarding-callout-icon-item"><IconBell size={18} />Push</div>
                <div className="onboarding-callout-icon-item"><IconCloud size={18} />Backup</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="onboarding-bottom-bar">
        <button className="onboarding-btn-primary" onClick={goNext}>{buttonLabel}</button>
      </div>
    </div>
  );
};

export default OnboardingFlow;
